/**
 * useSuggestedPrompts — web port of
 * `apps/WunderGuide/hooks/useSuggestedPrompts.ts`.
 *
 * Same auto-fetch + caching + hourly background refresh logic. Persists
 * the last successful response to localStorage so the next cold start can
 * paint instantly with stale-but-correct prompts.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchSuggestedPrompts,
  SuggestedPrompt,
  SuggestedPromptsContent,
} from '../api/contentApi';

const PROMPTS_CACHE_KEY = 'suggested_prompts_v1';

interface CachedPrompts {
  prompts: SuggestedPrompt[];
  headerText: string;
}

function readCache(): CachedPrompts | null {
  try {
    const raw = localStorage.getItem(PROMPTS_CACHE_KEY);
    return raw ? (JSON.parse(raw) as CachedPrompts) : null;
  } catch {
    return null;
  }
}

interface Options {
  sessionId: string | null;
  language?: string;
  destination?: string;
  limit?: number;
  enabled?: boolean;
  timeOfDay?: 'morning' | 'afternoon' | 'evening' | 'night';
  latitude?: number;
  longitude?: number;
}

const HOUR_MS = 60 * 60 * 1000;

export function useSuggestedPrompts(options: Options) {
  const {
    sessionId,
    language = 'en-US',
    destination = 'hawaii',
    limit = 5,
    enabled = true,
    timeOfDay,
    latitude,
    longitude,
  } = options;

  const cached = useRef(readCache()).current;
  const [prompts, setPrompts] = useState<SuggestedPrompt[]>(
    cached?.prompts ?? [],
  );
  const [headerText, setHeaderText] = useState<string>(
    cached?.headerText ?? 'Try asking…',
  );
  const [content, setContent] = useState<SuggestedPromptsContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const lastFetchRef = useRef(0);

  const fetchPrompts = useCallback(
    async (background = false) => {
      if (!enabled || !sessionId) return;
      if (!background) setLoading(true);
      setError(null);
      try {
        const result = await fetchSuggestedPrompts(sessionId, {
          language,
          destination,
          limit,
          timeOfDay,
          latitude,
          longitude,
        });
        setContent(result);
        setPrompts(result.prompts);
        const header = result.ui?.header || 'Try asking…';
        setHeaderText(header);
        lastFetchRef.current = Date.now();
        try {
          localStorage.setItem(
            PROMPTS_CACHE_KEY,
            JSON.stringify({ prompts: result.prompts, headerText: header }),
          );
        } catch {
          /* storage full / disabled — ignore */
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!background) setLoading(false);
      }
    },
    [
      enabled,
      sessionId,
      language,
      destination,
      limit,
      timeOfDay,
      latitude,
      longitude,
    ],
  );

  useEffect(() => {
    fetchPrompts(false);
  }, [fetchPrompts]);

  useEffect(() => {
    if (!enabled || !sessionId) return;
    const id = window.setInterval(() => {
      if (Date.now() - lastFetchRef.current >= HOUR_MS) {
        fetchPrompts(true);
      }
    }, 60_000);
    return () => window.clearInterval(id);
  }, [enabled, sessionId, fetchPrompts]);

  return {
    prompts,
    headerText,
    content,
    loading,
    error,
    globalConfig: content?.globalConfig,
    refresh: () => fetchPrompts(true),
  };
}

export default useSuggestedPrompts;
